import React, { useMemo } from 'react';

import { BiSearchAlt } from 'react-icons/bi';
import { Box, Typography } from '@mui/material';
import { allTokens } from '@hopdrive/ui/tokens';
import {
  defineGlobalSearch,
  type MobilityRouteRow,
  type MobilityRunAccess,
  type ScopeOption,
} from '@hopdrive/feature-global-search';
import DataCard from '@components/DataCard';
import { useCustomerConfig, useRooftops, useUser } from '@store';
import { useFeaturePermission } from '@utils/featurePermissions';
import { FeatureName } from '@utils/featureNames';
import { getRoleLabel, isAdminUser } from '@features/userManagement/utils/roleUtils';
import { useDealerScope } from '@features/learningCenter/hooks/useDealerScope';
import { useModuleCompletion } from '@features/learningCenter/hooks/useModuleCompletion';
import { resolveAssetUrl } from '@features/learningCenter/utils/assets';
import RooftopFilter from './components/RooftopFilter';
import { useSearchRooftopSwitch } from './hooks/useSearchRooftopSwitch';
import { productRootPath } from '@features/products/productRoutes';

/**
 * The dealer portal's global search configuration.
 *
 * The feature owns the capability — documents, entities, ranking, the results
 * page (#1237). This file is the dealer portal's whole contribution: its
 * rooftop scope, its route gates, its product access, and its house chrome.
 *
 * MUST stay module-constant: the `use*` fields below are called as hooks in a
 * fixed order. See defineGlobalSearch's contract.
 */

const MOBILITY_PRODUCTS = ['shuttle', 'mobile_service', 'parts_delivery'] as const;

/** A rooftop's effective_config, narrowed to the product flags read here. */
type ProductConfig = Record<string, { active?: boolean } | undefined>;

/**
 * Run detail routes are config-gated per rooftop (see Routes.tsx); a card links
 * only where the route is actually registered — search never links to a dead
 * end. Shuttle has no dealer detail route yet, so shuttle cards are info-only.
 *
 * `config` must be the run's OWN rooftop config, not the active one: clicking a
 * cross-rooftop result switches the active rooftop first, so the route that
 * matters is the target rooftop's.
 */
function runHref(run: MobilityRouteRow, config?: ProductConfig): string | undefined {
  if (run.product_type === 'parts_delivery' && config?.parts_delivery?.active === true) {
    return `/parts-delivery/runs/${run.id}`;
  }
  if (run.product_type === 'mobile_service' && config?.mobile_service?.active === true) {
    return `/mobile-service/runs/${run.id}`;
  }
  return undefined;
}

/**
 * Product access and run routes, read per rooftop (#1109).
 *
 * Scope is every rooftop the role can see, not just the active one: dealer
 * results are cross-rooftop by design (useSearchRooftopSwitch switches on
 * click), so reading the active rooftop's config alone would hide other
 * rooftops' runs entirely. This is why the product switches are read from each
 * rooftop's config rather than the portal products, whose snapshot covers the
 * working rooftop only.
 */
const useRunAccess = (): MobilityRunAccess => {
  const config = useCustomerConfig();
  const rooftops = useRooftops();

  const configByRooftop = useMemo(() => {
    const map = new Map<number, ProductConfig>();
    for (const rooftop of rooftops) {
      if (rooftop?.id != null && rooftop.effective_config) {
        map.set(rooftop.id, rooftop.effective_config as ProductConfig);
      }
    }
    return map;
  }, [rooftops]);

  // Plain dealers have no switcher list, so they fall back to the active
  // config — which is their only rooftop anyway.
  const scopedConfigs = useMemo(
    () => (configByRooftop.size ? [...configByRooftop.values()] : [config as ProductConfig | undefined]),
    [configByRooftop, config]
  );

  const products = useMemo(
    () => MOBILITY_PRODUCTS.filter(product => scopedConfigs.some(entry => entry?.[product]?.active === true)),
    [scopedConfigs]
  );

  return useMemo(
    () => ({
      products,
      href: run =>
        runHref(
          run,
          (run.customer?.id != null ? configByRooftop.get(run.customer.id) : undefined) ??
            (config as ProductConfig | undefined)
        ),
    }),
    [products, configByRooftop, config]
  );
};

/**
 * /users/:id and /drivers/:id are rooftop-scoped and block HopDrive admins —
 * mirror those route gates so search never links somewhere the user can't land.
 */
const useTeamRoutePermitted = () => {
  const user = useUser();
  const permitted = useFeaturePermission(FeatureName.USER_MANAGEMENT);
  return permitted && !isAdminUser(user?.profile?.default_role);
};

const useDriverRoutePermitted = () => {
  const user = useUser();
  const permitted = useFeaturePermission(FeatureName.DRIVER_MANAGEMENT);
  return permitted && !isAdminUser(user?.profile?.default_role);
};

/** /invoices/:id requires the invoices feature (Routes.tsx); mirror it (#1109). */
const useInvoicesPermitted = () => useFeaturePermission(FeatureName.INVOICES);

/**
 * Learning Center scope (#2325): the same tier / products / audiences the
 * library page filters with (useDealerScope). Null until the rooftop hydrates,
 * which the entity reads as "wait" rather than "show everything".
 */
const useTrainingScope = () => useDealerScope().scope;

/**
 * The signed-in user's completed module keys, from the library's shared
 * completion cache. Hooks run whether or not a search is active, so this costs
 * one small completions query when the header search mounts with the app frame
 * — the same query the library pages make, served from the same cache after.
 */
const useTrainingCompletedKeys = () => useModuleCompletion().completed;

/**
 * Rooftop filter options: exactly the rooftops the header selector fetched for
 * this user (power roles; empty for plain dealers — no picker, and a
 * hand-edited ?customer= outside the list is dropped).
 */
const useScopeOptions = (): ScopeOption[] => {
  const rooftops = useRooftops();
  return useMemo(
    () =>
      rooftops
        .filter(rooftop => rooftop?.id != null)
        .map(rooftop => ({ id: rooftop.id as number, name: rooftop?.name ?? `Rooftop ${rooftop.id}` })),
    [rooftops]
  );
};

/** Tenant vocabulary: the rooftop's own reference-number label in search hints. */
const useHintTerms = () => {
  const customerConfig = useCustomerConfig();
  const refLabel = customerConfig?.reference_num_label || 'reference #';
  return useMemo(() => ({ refLabel }), [refLabel]);
};

const PageShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ height: '100%', overflow: 'auto', p: 4 }}>{children}</Box>
);

const SearchEmptyState: React.FC<{ title: string; description: string }> = ({ title, description }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 8, px: 2 }}>
    <Box
      sx={{
        width: 96,
        height: 96,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: allTokens.palette.grey[100],
        color: allTokens.palette.grey[400],
        mb: 2,
      }}
    >
      <BiSearchAlt size={40} />
    </Box>
    <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.75, maxWidth: 440 }}>{description}</Typography>
  </Box>
);

export const dealerSearchConfig = defineGlobalSearch({
  // Candidates already covered by dealer Hasura permissions: appointments,
  // contacts, guest rides.
  entities: ['moves', 'guests', 'users', 'drivers', 'locations', 'lanes', 'mobility', 'training', 'invoices'],

  // No privileged reach: the dealer roles cannot read dispatchnotes at all
  // (only the admin and dispatcher roles are granted), and the recent-move enrichment is
  // an admin-desk affordance. See the feature's gql/privileged/README.md.

  options: {
    users: {
      label: 'Team',
      roleLabel: getRoleLabel,
      isInternalRole: isAdminUser,
      useEnabled: useTeamRoutePermitted,
    },
    drivers: { useEnabled: useDriverRoutePermitted },
    invoices: { useEnabled: useInvoicesPermitted },
    // Lanes: no dealer lane detail route yet, so lane results are info-only.
    mobility: { useRunAccess },
    // Learning Center (#2325): /learning-center/:key is auth-only (the nav item
    // is unconditional since #1197) — no gate to mirror (#1109). Visibility is
    // the library's own scope; completion comes from its shared cache.
    training: {
      href: module => `/learning-center/${encodeURIComponent(module.key)}`,
      useScope: useTrainingScope,
      useCompletedKeys: useTrainingCompletedKeys,
      resolveAssetUrl,
    },
  },

  useScopeOptions,
  useHintTerms,
  useResultNavigate: useSearchRooftopSwitch,

  page: {
    homeHref: productRootPath('moves'),
    scopeLabel: 'your rooftops',
    blankSlateDescription:
      'Search your moves, guests, team, drivers, invoices, and Learning Center training by name, phone number, email address, VIN, stock number, move ID, scenario ID, invoice ID, or topic. Press {shortcut} anywhere to start.',
    slots: {
      DataCard,
      PageShell,
      EmptyState: SearchEmptyState,
      RooftopFilter,
    },
  },
});
